import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { throwError, from } from 'rxjs';
// Avoid collision with HTML5 File model
import { File as CordovaFile, FileEntry } from '@ionic-native/file/ngx';
import { Platform } from '@ionic/angular';
import { switchMap } from 'rxjs/operators';

interface PathOptions {
  systemDir: string;
  path: string;
}

export interface DirOptions extends PathOptions {
  recursive?: boolean;
}

export interface FileOptions extends PathOptions {
  fileName: string;
  replace?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FileManagerService {
  constructor(
    private platform: Platform,
    private file: CordovaFile,
    private log: LogService
  ) {}

  private ready() {
    return from(this.platform.ready());
  }

  writeFile(blob: Blob, opts: FileOptions) {
    if (!opts || !opts.systemDir || !opts.path || !opts.fileName) {
      return throwError('System dir, path and filename must be specified');
    }
    return this.ready().pipe(switchMap(_ => from(this.saveBlob(blob, opts))));
  }

  private async saveBlob(blob: Blob, opts: FileOptions) {
    await this.createDirRecursivelyIfNotExists(opts.systemDir, opts.path).catch(
      err => {
        this.onCreateDirError(opts, err);
        throw err;
      }
    );
    const filePath = this.getFullPath(opts.systemDir, opts.path);
    this.log.i(this, `Writing file at: ${filePath}`);
    const resFileEntry = await this.file
      .writeFile(filePath, opts.fileName, blob, { replace: opts.replace })
      .catch(err => {
        this.onWriteFileError(opts, err);
        throw err;
      });
    this.log.i(this, `File written at: ${filePath}`);
    return resFileEntry as FileEntry;
  }

  private onWriteFileError(opts: FileOptions, err: any) {
    this.log.e(
      `Error writing file at ${opts.systemDir} => ${opts.path}. Err:`,
      err
    );
  }

  removeFile(opts: FileOptions) {
    if (!opts || !opts.systemDir || !opts.path || !opts.fileName) {
      return throwError('System dir, path and filename must be specified');
    }
    return this.ready().pipe(
      switchMap(_ =>
        from(this.rmFile(opts.systemDir, opts.path, opts.fileName))
      )
    );
  }

  async rmFile(systemDir: string, path: string, fileName: string) {
    this.log.i(this, `Removing file: ${systemDir} => ${path} ${fileName}`);

    const fullPath = this.getFullPath(systemDir, path);
    return this.file.removeFile(fullPath, fileName);
  }

  removeDir(opts: DirOptions) {
    if (!opts || !opts.systemDir || !opts.path) {
      return throwError('System dir and path must be specified');
    }
    return this.ready().pipe(
      switchMap(_ =>
        from(this.rmdir(opts.systemDir, opts.path, opts.recursive))
      )
    );
  }

  async rmdir(systemDir: string, path: string, recursive?: boolean) {
    this.log.i(this, `Removing folder: ${systemDir} => ${path}`);
    const existFolder = await this.existsFolder(systemDir, path);
    if (!existFolder) {
      this.log.i(this, 'Folder is missing');
      return Promise.resolve(true);
    }

    const fullPath = this.getFullPath(systemDir, path);
    const { dirPath, dirName } = this.decomposePath(fullPath);
    if (recursive) {
      await this.file.removeRecursively(dirPath, dirName);
    } else {
      await this.file.removeDir(dirPath, dirName);
    }
    this.log.i('Folder successfully removed', systemDir, path);
    return true;
  }

  createDir(opts: DirOptions) {
    if (!opts || !opts.systemDir || !opts.path) {
      return throwError('System dir and path must be specified');
    }
    return this.ready().pipe(
      switchMap(_ => {
        if (opts.recursive) {
          return from(
            this.createDirRecursivelyIfNotExists(opts.systemDir, opts.path)
          );
        } else {
          return from(this.createFolderIfNotExists(opts.systemDir, opts.path));
        }
      })
    );
  }

  private onCreateDirError(opts: FileOptions, err: any) {
    this.log.e(
      `Error creating directory at ${opts.systemDir} => ${opts.path}. Err:`,
      err
    );
  }

  private createDirRecursivelyIfNotExists(systemDir: string, path: string) {
    const pathLevels = this.getPathLevels(path);
    return pathLevels.reduce((prev, currentPathLevel) => {
      return prev.then(() => {
        return this.createFolderIfNotExists(systemDir, currentPathLevel);
      });
    }, Promise.resolve());
  }

  private getPathLevels(path: string) {
    let accumulatedLevel = '';
    const levelsList: string[] = [];
    (path || '').split('/').forEach(level => {
      accumulatedLevel += `/${level}`;
      // Paths don't start with slash
      levelsList.push(accumulatedLevel.substring(1));
    });
    return levelsList;
  }

  private async createFolderIfNotExists(systemDir: string, path: string) {
    const existFolder = await this.existsFolder(systemDir, path);
    this.log.i(
      this,
      `Creating new folder if not exists: ${systemDir} => ${path}: ${existFolder}`
    );
    if (!existFolder) {
      const fullPath = this.getFullPath(systemDir, path);
      const { dirPath, dirName } = this.decomposePath(fullPath);
      this.log.i(this, `Creating folder: ${dirPath} => ${dirName}`);
      return this.file.createDir(dirPath, dirName, false);
    }
  }

  private async existsFolder(systemDir: string, path: string) {
    this.log.i(this, `Checking folder: ${systemDir} => ${path}`);
    try {
      const fullPath = this.getFullPath(systemDir, path);
      const { dirPath, dirName } = this.decomposePath(fullPath);
      await this.file.checkDir(dirPath, dirName);
      return true;
    } catch (e) {
      return false;
    }
  }

  getFullPath(systemDir: string, path: string) {
    return `${systemDir}${path}`;
  }

  private decomposePath(path: string) {
    return {
      dirPath: (path || '').substring(0, path.lastIndexOf('/') + 1),
      dirName: (path || '').substring(path.lastIndexOf('/') + 1)
    };
  }
}
